/**
 * Public (staff-gated) Quarter-at-a-Glance page at /qtr/:token.
 * Anyone with an @medicysltd.co.uk email passes the gate; the page
 * renders the frozen published snapshot as a magazine-style update:
 * dark hero → highlights / lowlights / looking ahead → KPI board →
 * the ONE priority → the quarter in words (AI narrative + plan).
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowRight, Loader2, Lock, Sun, CloudRain, Telescope } from "lucide-react";
import QuarterKpis from "../components/quarter/QuarterKpis";
import { looksLikeHtml, RICH_STYLES } from "../components/quarter/RichTextEditor";

const API = process.env.REACT_APP_BACKEND_URL;

function GateScreen({ token, onPassed }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/api/public/quarter/${token}/gate`, { email });
      onPassed();
    } catch (err) {
      const reason = err?.response?.data?.detail?.reason;
      setError(
        reason === "personal_domain"
          ? "Please use your Medicys work email."
          : reason === "invalid_email"
            ? "That doesn't look like a valid email."
            : "This update is for the Medicys team - please use your @medicysltd.co.uk email.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="quarter-gate-screen">
      <div className="w-full max-w-md rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur-xl p-8">
        <p className="font-serif text-2xl text-white">Medicys</p>
        <div className="flex items-center gap-2 mt-6 text-stone-400">
          <Lock className="w-4 h-4" />
          <p className="text-sm">This quarterly update is for the Medicys team.</p>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@medicysltd.co.uk"
            data-testid="quarter-gate-email-input"
            className="w-full rounded-xl bg-white/10 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-teal-400/50"
          />
          {error && (
            <p className="text-xs text-rose-400" data-testid="quarter-gate-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="quarter-gate-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-teal-500 hover:bg-teal-400 transition-colors px-4 py-3 text-sm font-medium text-slate-950 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            View the update
          </button>
        </form>
      </div>
    </div>
  );
}

function BulletColumn({ icon: Icon, title, tone, accent, items, testid }) {
  const isRich = typeof items === "string";
  const hasContent = isRich
    ? items.replace(/<[^>]*>/g, "").trim().length > 0
    : (items || []).length > 0;
  return (
    <div
      className={`rounded-2xl border border-[#1B4F8A]/10 border-l-4 ${accent} bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-6 sm:p-7 sm:grid sm:grid-cols-[180px_1fr] sm:gap-x-8 q-card q-reveal`}
      data-testid={testid}
    >
      <div className={`inline-flex items-start gap-2.5 text-xs font-mono uppercase tracking-widest ${tone} pt-1`}>
        <Icon className="w-5 h-5 shrink-0" />
        <span className="pt-0.5">{title}</span>
      </div>
      <div className="mt-3 sm:mt-0">
        {!hasContent ? (
          <p className="text-xs text-stone-400">—</p>
        ) : isRich ? (
          <div
            className="qtr-rich"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(items) }}
          />
        ) : (
          <ul className="space-y-2">
            {items.map((s, i) => (
              <li key={i} className="text-sm text-slate-800 leading-relaxed pl-3 border-l-2 border-stone-200">
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SummaryBlocks({ text }) {
  const blocks = [];
  let bullets = null;
  String(text || "").split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      bullets = null;
      return;
    }
    if (line.startsWith("## ")) {
      bullets = null;
      blocks.push({ t: "h", v: line.slice(3).trim() });
    } else if (/^[-•*]\s+/.test(line)) {
      if (!bullets) {
        bullets = [];
        blocks.push({ t: "ul", v: bullets });
      }
      bullets.push(line.replace(/^[-•*]\s+/, ""));
    } else {
      bullets = null;
      blocks.push({ t: "p", v: line });
    }
  });
  return (
    <div className="mt-3 space-y-3">
      {blocks.map((b, i) =>
        b.t === "h" ? (
          <h3 key={i} className="font-serif text-xl text-slate-900 pt-2">{b.v}</h3>
        ) : b.t === "ul" ? (
          <ul key={i} className="space-y-2">
            {b.v.map((s, j) => (
              <li key={j} className="text-sm text-slate-700 leading-relaxed pl-3 border-l-2 border-[#1f7468]/25">
                {s}
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className="text-sm text-slate-700 leading-relaxed">{b.v}</p>
        ),
      )}
    </div>
  );
}

export default function QuarterPublic() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | gate | content | notfound
  const [snap, setSnap] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/quarter/${token}`);
      setSnap(data);
      setPhase("content");
    } catch (err) {
      if (err?.response?.status === 403) setPhase("gate");
      else setPhase("notfound");
    }
  }, [token]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-stone-500 animate-spin" />
      </div>
    );
  }
  if (phase === "notfound") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <p className="text-stone-400 text-sm" data-testid="quarter-not-found">
          This update isn't available. Check the link with the sender.
        </p>
      </div>
    );
  }
  if (phase === "gate") {
    return <GateScreen token={token} onPassed={fetchContent} />;
  }

  const e = snap.editorial || {};
  return (
    <div
      className="min-h-screen bg-[#0f2036]"
      style={{ "--q-heading": "#f5f7fa", "--q-sub": "#9fb0c8", "--q-num": "#7fd1c7", "--q-line": "rgba(255,255,255,0.16)" }}
      data-testid="quarter-public-page"
    >
      <style>{`
        .q-hin { opacity: 0; animation: qhin .7s ease-out forwards; }
        @keyframes qhin { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .q-hin { animation: none; opacity: 1; } }
      `}</style>
      {/* hero */}
      <header className="bg-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle at 15% 25%, #0f172a 1px, transparent 1px)", backgroundSize: "24px 24px" }}
        />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-10 sm:pb-12 relative">
          <video
            src="/assets/medicys-globe.mp4"
            poster="/assets/medicys-globe.jpg"
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            data-testid="quarter-hero-illustration"
            className="hidden lg:block absolute right-8 bottom-2 h-64 w-auto pointer-events-none select-none mix-blend-multiply q-hin"
            style={{ animationDelay: ".35s" }}
          />
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#1f7468] q-hin">
            Medicys · quarterly team update
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mt-3 leading-tight text-[#152b45] q-hin" style={{ animationDelay: ".15s" }}>
            {snap.label} <span className="text-stone-400">at a glance</span>
          </h1>
          <p className="text-sm text-stone-500 mt-4 max-w-xl leading-relaxed q-hin" style={{ animationDelay: ".3s" }}>
            A detailed look at how we performed in the quarter, what drove our results, where we
            made progress, where pressures and challenges remain, and what we need to focus on as
            we move forward.
          </p>
          {snap.published_at && (
            <p className="text-xs text-stone-400 mt-3 font-mono q-hin" style={{ animationDelay: ".45s" }}>
              Published {new Date(snap.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-8 pb-10 space-y-14">
        <style>{RICH_STYLES}</style>
        {/* at a glance cards */}
        <section className="flex flex-col gap-4 relative z-10">
          <BulletColumn icon={Sun} title="Highlights" tone="text-[#1f7468]" accent="border-l-[#1f7468]" items={e.highlights} testid="quarter-highlights" />
          <BulletColumn icon={CloudRain} title="Lowlights" tone="text-[#9b2226]" accent="border-l-[#9b2226]" items={e.lowlights} testid="quarter-lowlights" />
          <BulletColumn icon={Telescope} title="Looking ahead" tone="text-[#1B4F8A]" accent="border-l-[#1B4F8A]" items={e.looking_ahead} testid="quarter-ahead" />
        </section>

        {/* KPI board */}
        <section>
          <QuarterKpis kpis={snap.kpis} takeaways={snap.takeaways} />
        </section>

        {/* ONE priority */}
        {(e.one_priority_title || e.one_priority) && (
          <section
            className="rounded-3xl border border-[#1B4F8A]/10 border-l-4 border-l-[#d6a419] bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-6 sm:p-8 q-card q-reveal"
            data-testid="quarter-priority"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-400">
              The ONE priority
            </p>
            {e.one_priority_title && (
              <h2 className="font-serif text-3xl text-slate-900 mt-2">{e.one_priority_title}</h2>
            )}
            <p className="text-sm text-slate-700 leading-relaxed mt-3 whitespace-pre-line">
              {e.one_priority}
            </p>
          </section>
        )}

        {/* AI-drafted quarter narrative + plan of action */}
        {(e.ai_summary || "").replace(/<[^>]*>/g, "").trim() && (
          <section
            className="rounded-3xl border border-[#1B4F8A]/10 border-l-4 border-l-[#1f7468] bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-6 sm:p-8 mb-8 q-card q-reveal"
            data-testid="quarter-ai-summary"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-400">
              The quarter in words
            </p>
            {looksLikeHtml(e.ai_summary) ? (
              <div
                className="qtr-rich mt-3"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(e.ai_summary) }}
              />
            ) : (
              <SummaryBlocks text={e.ai_summary} />
            )}
            {e.signature && (
              <p className="font-serif text-xl text-slate-900 mt-6">— {e.signature}</p>
            )}
          </section>
        )}

      </main>

      <footer className="border-t border-white/10 py-6">
        <p className="text-center text-[10px] font-mono uppercase tracking-widest text-stone-400">
          Internal - for the Medicys team only
        </p>
      </footer>
    </div>
  );
}
